import { useState } from 'react';
import { regionOptions, yearOptions, sortOptions } from '@/mocks/categories';

export type SortValue = 'latest' | 'rating' | 'hot' | 'year';

export interface FilterState {
  regions: string[];
  years: number[];
  minRating: number;
  sort: SortValue;
}

export const defaultFilter: FilterState = {
  regions: [],
  years: [],
  minRating: 0,
  sort: 'latest',
};

const ratingOptions = [
  { value: 0, label: '不限' },
  { value: 9, label: '9 分以上' },
  { value: 8, label: '8 分以上' },
  { value: 7, label: '7 分以上' },
];

interface FilterBarProps {
  value: FilterState;
  onChange: (next: FilterState) => void;
}

export default function FilterBar({ value, onChange }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const toggleRegion = (region: string) => {
    const next = value.regions.includes(region)
      ? value.regions.filter((r) => r !== region)
      : [...value.regions, region];
    onChange({ ...value, regions: next });
  };

  const toggleYear = (year: number) => {
    const next = value.years.includes(year)
      ? value.years.filter((y) => y !== year)
      : [...value.years, year];
    onChange({ ...value, years: next });
  };

  const hasActive =
    value.regions.length > 0 || value.years.length > 0 || value.minRating > 0 || value.sort !== 'latest';

  const pill = (active: boolean) =>
    `px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
      active
        ? 'bg-primary-500 text-background-50'
        : 'bg-background-100 text-foreground-700 hover:bg-background-200'
    }`;

  return (
    <div className="bg-background-50 border border-background-200 rounded-lg">
      <div className="flex items-center justify-between px-4 py-3 md:hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-800 cursor-pointer"
        >
          <i className="ri-filter-3-line"></i>
          筛选条件
          {hasActive && <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>}
        </button>
        <button
          type="button"
          onClick={() => onChange(defaultFilter)}
          className="text-sm text-foreground-500 cursor-pointer"
        >
          重置
        </button>
      </div>

      <div className={`${open ? 'block' : 'hidden'} md:block px-4 pb-4 md:py-4 space-y-4`}>
        <div className="flex flex-col md:flex-row md:items-start gap-2 md:gap-4">
          <span className="text-sm text-foreground-500 md:w-16 shrink-0 md:pt-1.5">地区</span>
          <div className="flex flex-wrap gap-2">
            {regionOptions.map((region) => (
              <button
                key={region}
                type="button"
                onClick={() => toggleRegion(region)}
                className={pill(value.regions.includes(region))}
              >
                {region}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-start gap-2 md:gap-4">
          <span className="text-sm text-foreground-500 md:w-16 shrink-0 md:pt-1.5">年份</span>
          <div className="flex flex-wrap gap-2">
            {yearOptions.map((year) => (
              <button
                key={year}
                type="button"
                onClick={() => toggleYear(year)}
                className={pill(value.years.includes(year))}
              >
                {year}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-start gap-2 md:gap-4">
          <span className="text-sm text-foreground-500 md:w-16 shrink-0 md:pt-1.5">评分</span>
          <div className="flex flex-wrap gap-2">
            {ratingOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onChange({ ...value, minRating: option.value })}
                className={pill(value.minRating === option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-start gap-2 md:gap-4">
          <span className="text-sm text-foreground-500 md:w-16 shrink-0 md:pt-1.5">排序</span>
          <div className="flex flex-wrap gap-2">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onChange({ ...value, sort: option.value as SortValue })}
                className={pill(value.sort === option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="hidden md:flex justify-end">
          <button
            type="button"
            onClick={() => onChange(defaultFilter)}
            className="inline-flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}